import { AlertCircle } from "lucide-react";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";

interface ErrorAlertProps {
  /** The error object or error message */
  error?: Error | string | null;
  /** Custom title for the alert */
  title?: string;
  /** Custom description for the alert */
  description?: string;
  /** Whether to show the alert even when error is null */
  show?: boolean;
}

/**
 * Default error alert component for displaying loading/fetch errors across all pages.
 * Uses generic messaging that works for most loading error scenarios.
 *
 * @example
 * ```tsx
 * // Simple usage with defaults
 * <ErrorAlert error={error} />
 *
 * // Custom messaging if needed
 * <ErrorAlert
 *   error={error}
 *   title="Custom Title"
 *   description="Custom description"
 * />
 * ```
 */
export function ErrorAlert({
  error,
  title = "Unable to Load Data",
  description = "We're having trouble loading this information. This might be due to a temporary server issue. Please try refreshing the page or contact support if the problem continues.",
  show = false,
}: ErrorAlertProps) {
  if (!error && !show) {
    return null;
  }

  return (
    <Alert variant="destructive">
      <AlertCircle className="h-4 w-4" />
      <AlertTitle>{title}</AlertTitle>
      <AlertDescription>
        {description}
        {error && typeof error === "string" && (
          <div className="mt-2 text-sm opacity-80">Error: {error}</div>
        )}
        {error instanceof Error && (
          <div className="mt-2 text-sm opacity-80">Error: {error.message}</div>
        )}
      </AlertDescription>
    </Alert>
  );
}
